import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type { ComponentLike } from '@glint/template';
import type { Body, Meta, UppyFile as UppyCoreFile } from '@uppy/core';
// linced-common Imports
import AsyncButtonComponent from '@linc-technologies/linced-common/components/async-button';
import FileUploaderComponent from '@linc-technologies/linced-common/components/file-uploader';
import MediaImageComponent from '@linc-technologies/linced-common/components/media-image';
import PostAttachmentComponent from '@linc-technologies/linced-common/components/post-attachment';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import type Media from '@linc-technologies/linced-common/models/media';
import type GoogleDriveService from '@linc-technologies/linced-common/services/google-drive';
import type SessionService from '@linc-technologies/linced-common/services/session';
import type StoreService from '@linc-technologies/linced-common/services/store';
import type ToasterService from '@linc-technologies/linced-common/services/toaster';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';
// External Imports
import { task } from 'ember-concurrency';
import PaperButton from 'ember-paper/components/paper-button';
import PaperIcon from 'ember-paper/components/paper-icon';
import { not } from 'ember-truth-helpers';

// UppyFile is an uploaded Uppy file.
type UppyFile = UppyCoreFile<Meta, Body>;

// TS-STANDIN(components/async-button, file-uploader, media-image,
// post-attachment): these components are JS, so give them the signature that
// media-picker uses.
const AsyncButton = AsyncButtonComponent as unknown as ComponentLike<{
  Args: { loading?: boolean; loadingMessage?: string; onClick?: () => void };
  Blocks: { default: [] };
  Element: HTMLButtonElement;
}>;
const FileUploader = FileUploaderComponent as unknown as ComponentLike<{
  Args: {
    clickPrompt?: string;
    dropPrompt?: string;
    empty?: boolean;
    onDashboardClose?: () => void;
    onError?: (file: UppyFile, err: unknown) => void;
    onUpload?: (
      file: UppyFile,
      upload: Parameters<typeof getKeyAndSourceFromUpload>[0],
    ) => void;
    onUploadStart?: () => void;
  };
  Blocks: { default: [] };
}>;
const MediaImage = MediaImageComponent as unknown as ComponentLike<{
  Args: { mediaId?: string | null };
  Element: HTMLImageElement;
}>;
const PostAttachment = PostAttachmentComponent as unknown as ComponentLike<{
  Args: { recordOverride?: Media | null };
}>;

// MediaFields are the fields of a new media record.
type MediaFields = Record<string, unknown>;

interface MediaPickerSignature {
  Element: HTMLDivElement;
  Args: {
    // dropPrompt provides the prompt to display in the uppy drop target.
    dropPrompt?: string;
    // media provides a media record, or empty, for initial display if
    // uploaded content already exists.
    media?: Media | null;
    // onUploadStart provides a function to call when an upload has started,
    // or the google picker has loaded.
    onUploadStart?: () => void;
    // onUploadCancel provides a function to call when the upload has been
    // cancelled.
    onUploadCancel?: () => void;
    // onUploadRemoved provides a function to call to deal with the logic of
    // removing the media.
    onUploadRemoved?: () => void;
    // onUploadFinished provides a function to call when the media record has
    // been created.
    // @param media {media} - media provides a meta record.
    onUploadFinished?: (media: Media) => void;
  };
}

/**
 * media-picker provides a way to upload a single file (file, image or embed).
 * It supports uploading via Uppy or embedding via the Google Drive picker.
 *
 * It uploads and creates a media record for the provided media. It returns the
 * created media record via the received onUploadFinished callback.
 *
 * Discarding unused media records when a user is rotating through different
 * files or pushes in the wrong file is the parent components responsibility.
 *
 */
export default class MediaPicker extends Component<MediaPickerSignature> {
  // Services

  @service declare googleDrive: GoogleDriveService;

  @service declare session: SessionService;

  @service declare store: StoreService;

  @service declare toaster: ToasterService;

  // State

  @tracked
  uploading = false;

  // Public methods

  get dropPrompt() {
    return this.args.dropPrompt ?? 'Drop file here, or %{browse}';
  }

  // stagedMedia is the media to show. The parent passes the created media
  // back in as @media after onUploadFinished.
  get stagedMedia() {
    return this.args.media ?? null;
  }

  // isEmbedded calculates whether the staged media is an embed.
  get isEmbedded() {
    return this.stagedMedia?.mediaType === MediaType.EMBED;
  }

  // thumbnailUri gives the thumbnail of an embed or a Google Drive file. Other
  // media has no URL on the record: the template shows it with MediaImage,
  // which loads the image blob.
  get thumbnailUri() {
    const media = this.stagedMedia;
    if (!media) {
      return '';
    }

    if (
      media.mediaType === MediaType.EMBED ||
      media.mediaSource === MediaSource.GOOGLE_DRIVE
    ) {
      return media.thumbnailUrl;
    }

    return '';
  }

  // Page media upload handling.
  startUpload = () => {
    this.args.onUploadStart?.();
    this.uploading = true;
  };

  // handleUpload creates a media record for the newly uploaded file.
  handleUpload = (
    file: UppyFile,
    upload: Parameters<typeof getKeyAndSourceFromUpload>[0],
  ) => {
    const { key, mediaSource } = getKeyAndSourceFromUpload(upload);
    void this.createMediaTask.perform({
      schoolId: this.session.school,
      personId: this.session.person,
      sharedWith: SharingLevel.SCHOOL,
      mediaType: MediaType.IMAGE,
      mediaSource: mediaSource,
      mimeType: file.type,
      fileName: file.name,
      storageKey: key,
      fileSize: file.size,
    });
  };

  // handleUploadError
  handleUploadError = (file: UppyFile) => {
    this._notifyFailed(file.name);
  };

  // launchGooglePicker opens the Google Picker for embedding files.
  launchGooglePicker = () => {
    void this.embedFromDriveTask.perform();
  };

  // noOp provides a no-op function for components that require a callback
  // function.
  noOp = () => {};

  /**
   * embedFromDriveTask lets the user pick Google Drive files, and embeds them,
   * in order to return a single media record on success. It stops if the
   * component is destroyed.
   */
  embedFromDriveTask = task(async () => {
    let files: google.picker.DocumentObject[];
    try {
      files = await this.googleDrive.pickFiles({
        onLoaded: () => {
          // This action doesn't seem to be documented...
          // loaded is returned as an action when the picker is ready to pick.
          //
          // Setting upload true here is less abrasive to the user compared to
          // having the upload spinner running in the background while they are
          // choosing their google account or connecting to google drive in the
          // pop-up window (...it's surprisingly distracting...)
          //
          // Worst that could happen if this action is deprecated by the picker
          // is that the spinner won't appear while the file is uploading in the
          // background, in which case we could move to the spinner being on the
          // whole time by setting 'upload true' before calling pickFiles.
          this.args.onUploadStart?.();
          this.uploading = true;
        },
      });
    } catch (err) {
      // Google did not give a token, or the Picker did not load.
      this._embedFailed(err);
      return;
    }

    if (files.length === 0) {
      // The user closed the picker.
      this.args.onUploadCancel?.();
      this.uploading = false;
      return;
    }

    for (const file of files) {
      try {
        // No onShareFailed: a file nobody else can open is not embedded.
        const media = await this.googleDrive.embedFile(file);
        if (!media) {
          // The user declined the sharing prompt.
          this._embedFailed({ userCancelled: true }, file);
          continue;
        }

        await this.createMediaTask.perform({
          ...media,
          schoolId: this.session.school,
          personId: this.session.person,
          sharedWith: SharingLevel.SCHOOL,
        });
      } catch (err) {
        this._embedFailed(err, file);
      }
    }
  });

  /**
   * createMediaTask creates a new media record, notifies and runs callbacks
   * as required. It stops if the component is destroyed.
   *
   * @param fields - the fields of the new media record.
   */
  createMediaTask = task(async (fields: MediaFields) => {
    try {
      const record = this.store.createRecord<Media>('media', fields);
      // The store.request() migration is separate work.
      // eslint-disable-next-line warp-drive/no-legacy-request-patterns
      const media = await record.save();
      this.toaster.success(`Uploaded ${media.fileName} successfully!`);
      this.args.onUploadFinished?.(media);
    } catch {
      this._notifyFailed(fields['fileName'] as string | undefined);
    } finally {
      this.uploading = false;
    }
  });

  // Private methods

  // _notifyFailed informs the user that the upload failed via a toast.
  //
  // @param fileName - optional. The name of the file that failed.
  _notifyFailed(fileName?: string) {
    const subject = fileName ? `"${fileName}"` : 'that file';
    this.toaster.error(`We couldn't upload ${subject}. Try again.`);
  }

  /**
   * _embedFailed handles a Google Drive embed that could not be completed, so
   * that the picker always stops spinning and the user is told why. No media
   * record is created: an embed nobody else can open is worse than none.
   *
   * @param err - the failure. `userCancelled` marks the user declining the
   *     sharing prompt, which needs no toast.
   * @param file - optional. The file that was not embedded.
   */
  _embedFailed(err: unknown, file?: google.picker.DocumentObject) {
    this.uploading = false;
    this.args.onUploadCancel?.();
    this.googleDrive.showEmbedError(err, file);
  }

  <template>
    {{!
      The outer div is the wrapper element that the classic component had. It
      keeps the picker its content height in the callers' flex rows: without
      it, the Embed from Drive button stretches to the height of the row.
    }}
    <div ...attributes>
      {{!
      we flip the layout style based on stagedMedia, so that the remove content
      button sits below the thumbnail images.
    }}
      <div class="{{if this.stagedMedia 'layout-column' 'layout-row'}} flex">
        <div
          class="{{if this.stagedMedia 'layout-column' 'layout-row'}}
            layout-align-center-start admin-page-modal-media
            {{if this.isEmbedded '' 'md-padding'}}
            {{if this.uploading 'is-uploading hide'}}"
        >
          {{#if this.isEmbedded}}
            <PostAttachment @recordOverride={{this.stagedMedia}} />
          {{else}}
            <FileUploader
              @clickPrompt="browse"
              @dropPrompt={{this.dropPrompt}}
              @empty={{not this.stagedMedia}}
              @onDashboardClose={{this.noOp}}
              @onError={{this.handleUploadError}}
              @onUpload={{this.handleUpload}}
              @onUploadStart={{this.startUpload}}
            >
              {{#if this.thumbnailUri}}
                <img
                  alt={{this.stagedMedia.fileName}}
                  src={{this.thumbnailUri}}
                  loading="lazy"
                />
              {{else if this.stagedMedia}}
                <MediaImage
                  @mediaId={{this.stagedMedia.id}}
                  alt={{this.stagedMedia.fileName}}
                  loading="lazy"
                />
              {{/if}}
            </FileUploader>
          {{/if}}
        </div>

        {{#if this.stagedMedia}}
          <div
            class="{{if this.stagedMedia 'layout-column' 'layout-row'}}
              layout-align-center-start"
          >
            <PaperButton
              @onClick={{@onUploadRemoved}}
              @raised={{true}}
              @warn={{true}}
            >
              Remove Content
            </PaperButton>
          </div>
        {{else if this.googleDrive.isPickerSupported}}
          <AsyncButton
            data-test-embed-from-drive
            @loading={{this.uploading}}
            @loadingMessage="uploading..."
            @onClick={{this.launchGooglePicker}}
          >
            <PaperIcon @icon="google-drive" @size={{18}} />
            Embed from Drive
          </AsyncButton>
        {{/if}}
      </div>
    </div>
  </template>
}
